import { useState } from "react";
import {
	useQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import {
	Timer,
	RefreshCw,
	Play,
	Calendar,
	AlertTriangle,
	CheckCircle2,
	XCircle,
	Clock,
	ChevronLeft,
	ChevronRight,
	Trash2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { getAccessToken } from "@/services/auth";
import { adminApiUrl } from "@/config";
import { useToast } from "@/components/ui/use-toast";
import { CronHealthBoard } from "./CronHealthBoard";

// --- Helpers ---

function formatDuration(ms: number): string {
	if (ms < 1000) return `${ms}ms`;
	if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
	return `${(ms / 60000).toFixed(1)}m`;
}

function formatTimeAgo(date: string): string {
	const diff = Date.now() - new Date(date).getTime();
	const mins = Math.floor(diff / 60000);
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	return `${Math.floor(hours / 24)}d ago`;
}

const statusColors: Record<string, string> = {
	completed: "bg-green-100 text-green-700",
	partial: "bg-yellow-100 text-yellow-700",
	failed: "bg-red-100 text-red-700",
	running: "bg-blue-100 text-blue-700",
};

const statusIcons: Record<string, React.ReactNode> = {
	completed: <CheckCircle2 className="w-4 h-4 text-green-500" />,
	partial: <AlertTriangle className="w-4 h-4 text-yellow-500" />,
	failed: <XCircle className="w-4 h-4 text-red-500" />,
	running: <Clock className="w-4 h-4 text-blue-500" />,
};

// --- API functions ---

async function fetchJobSummary() {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/job-monitor/summary"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchJobLogs(filters: {
	jobName: string;
	status: string;
	page: number;
}) {
	const token = getAccessToken();
	const params = new URLSearchParams();
	if (filters.jobName) params.set("jobName", filters.jobName);
	if (filters.status) params.set("status", filters.status);
	params.set("page", String(filters.page));
	params.set("limit", "20");
	const res = await fetch(
		adminApiUrl(`/job-monitor/logs?${params.toString()}`),
		{ headers: { Authorization: `Bearer ${token}` } },
	);
	const data = await res.json();
	return data.data;
}

async function fetchFailingOrgs(jobName?: string) {
	const token = getAccessToken();
	const params = new URLSearchParams();
	if (jobName) params.set("jobName", jobName);
	const res = await fetch(
		adminApiUrl(`/job-monitor/failing-orgs?${params.toString()}`),
		{ headers: { Authorization: `Bearer ${token}` } },
	);
	const data = await res.json();
	return data.data;
}

async function fetchOrganizations() {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/organizations?limit=100"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function triggerJob(payload: {
	type: string;
	organizationId: string;
	targetDate: string;
}) {
	const token = getAccessToken();
	const res = await fetch(
		adminApiUrl(`/job-monitor/trigger/${payload.type}`),
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				organizationId: payload.organizationId,
				targetDate: payload.targetDate,
			}),
		},
	);
	const data = await res.json();
	if (!res.ok) throw new Error(data.message || "Trigger failed");
	return data;
}

async function backfillJob(payload: {
	type: string;
	organizationId: string;
	startDate: string;
	endDate: string;
}) {
	const token = getAccessToken();
	const res = await fetch(
		adminApiUrl(`/job-monitor/backfill/${payload.type}`),
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				organizationId: payload.organizationId,
				startDate: payload.startDate,
				endDate: payload.endDate,
			}),
		},
	);
	const data = await res.json();
	if (!res.ok) throw new Error(data.message || "Backfill failed");
	return data;
}

async function previewCleanup(payload: {
	olderThanDays: number;
	jobName?: string;
	status?: string;
}) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/job-monitor/cleanup/preview"), {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(payload),
	});
	const data = await res.json();
	if (!res.ok) throw new Error(data.message || "Preview failed");
	return data.data;
}

async function executeCleanup(payload: {
	olderThanDays: number;
	jobName?: string;
	status?: string;
}) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/job-monitor/cleanup"), {
		method: "DELETE",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(payload),
	});
	const data = await res.json();
	if (!res.ok) throw new Error(data.message || "Cleanup failed");
	return data.data;
}

// --- Component ---

export function JobMonitor() {
	const { toast } = useToast();
	const queryClient = useQueryClient();

	// State
	const [jobNameFilter, setJobNameFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const [page, setPage] = useState(1);
	const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
	const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
	const [drillDownRun, setDrillDownRun] = useState<any>(null);

	// Trigger dialog state
	const [triggerType, setTriggerType] = useState("attendance");
	const [triggerOrgId, setTriggerOrgId] = useState("");
	const [triggerDate, setTriggerDate] = useState("");

	// Backfill dialog state
	const [backfillType, setBackfillType] = useState("attendance");
	const [backfillOrgId, setBackfillOrgId] = useState("");
	const [backfillStartDate, setBackfillStartDate] = useState("");
	const [backfillEndDate, setBackfillEndDate] = useState("");

	// Cleanup dialog state
	const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
	const [cleanupDays, setCleanupDays] = useState("30");
	const [cleanupJobName, setCleanupJobName] = useState("");
	const [cleanupStatus, setCleanupStatus] = useState("");
	const [cleanupPreviewCount, setCleanupPreviewCount] = useState<
		number | null
	>(null);
	const [cleanupConfirmed, setCleanupConfirmed] = useState(false);

	// Queries
	const {
		data: summary,
		isLoading: summaryLoading,
		refetch: refetchSummary,
	} = useQuery({
		queryKey: ["job-monitor-summary"],
		queryFn: fetchJobSummary,
		refetchInterval: 15000,
	});

	const { data: logs, isLoading: logsLoading } = useQuery({
		queryKey: ["job-monitor-logs", jobNameFilter, statusFilter, page],
		queryFn: () =>
			fetchJobLogs({
				jobName: jobNameFilter,
				status: statusFilter,
				page,
			}),
		refetchInterval: 15000,
	});

	const { data: failingOrgs } = useQuery({
		queryKey: ["job-monitor-failing", jobNameFilter],
		queryFn: () => fetchFailingOrgs(jobNameFilter),
	});

	const { data: organizations } = useQuery({
		queryKey: ["organizations-list"],
		queryFn: fetchOrganizations,
		enabled: triggerDialogOpen || backfillDialogOpen,
	});

	// Mutations
	const triggerMutation = useMutation({
		mutationFn: triggerJob,
		onSuccess: () => {
			toast({ title: "Job triggered successfully", variant: "default" });
			setTriggerDialogOpen(false);
			setTriggerOrgId("");
			setTriggerDate("");
			queryClient.invalidateQueries({ queryKey: ["job-monitor-summary"] });
			queryClient.invalidateQueries({ queryKey: ["job-monitor-logs"] });
		},
		onError: (err: Error) => {
			toast({ title: err.message, variant: "destructive" });
		},
	});

	const backfillMutation = useMutation({
		mutationFn: backfillJob,
		onSuccess: () => {
			toast({ title: "Backfill started successfully", variant: "default" });
			setBackfillDialogOpen(false);
			setBackfillType("attendance");
			setBackfillOrgId("");
			setBackfillStartDate("");
			setBackfillEndDate("");
			queryClient.invalidateQueries({ queryKey: ["job-monitor-summary"] });
			queryClient.invalidateQueries({ queryKey: ["job-monitor-logs"] });
		},
		onError: (err: Error) => {
			toast({ title: err.message, variant: "destructive" });
		},
	});

	const cleanupPreviewMutation = useMutation({
		mutationFn: previewCleanup,
		onSuccess: (data) => {
			setCleanupPreviewCount(data.count);
		},
		onError: (err: Error) => {
			toast({ title: err.message, variant: "destructive" });
		},
	});

	const cleanupMutation = useMutation({
		mutationFn: executeCleanup,
		onSuccess: (data) => {
			toast({
				title: `Deleted ${data.deletedCount} logs`,
				variant: "default",
			});
			setCleanupDialogOpen(false);
			setCleanupPreviewCount(null);
			setCleanupConfirmed(false);
			queryClient.invalidateQueries({ queryKey: ["job-monitor-summary"] });
			queryClient.invalidateQueries({ queryKey: ["job-monitor-logs"] });
		},
		onError: (err: Error) => {
			toast({ title: err.message, variant: "destructive" });
		},
	});

	const isLoading = summaryLoading || logsLoading;

	const backfillDays =
		backfillStartDate && backfillEndDate
			? Math.max(
					0,
					Math.ceil(
						(new Date(backfillEndDate).getTime() -
							new Date(backfillStartDate).getTime()) /
							(1000 * 60 * 60 * 24),
					) + 1,
				)
			: 0;

	const orgList = Array.isArray(organizations)
		? organizations
		: organizations?.organizations || [];

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<Timer className="w-6 h-6" />
						Job Monitor
					</h1>
					<p className="text-gray-500 mt-1">
						Monitor scheduled job executions and trigger manual runs
					</p>
				</div>
				<div className="flex gap-2">
					<Button
						variant="outline"
						onClick={() => {
							setCleanupDialogOpen(true);
							setCleanupPreviewCount(null);
							setCleanupConfirmed(false);
						}}
					>
						<Trash2 className="w-4 h-4 mr-2" />
						Cleanup
					</Button>
					<Button
						variant="outline"
						onClick={() => setBackfillDialogOpen(true)}
					>
						<Calendar className="w-4 h-4 mr-2" />
						Backfill
					</Button>
					<Button
						variant="outline"
						onClick={() => setTriggerDialogOpen(true)}
					>
						<Play className="w-4 h-4 mr-2" />
						Trigger Job
					</Button>
					<Button
						variant="outline"
						onClick={() => {
							refetchSummary();
							queryClient.invalidateQueries({
								queryKey: ["job-monitor-logs"],
							});
						}}
						disabled={isLoading}
					>
						<RefreshCw
							className={cn(
								"w-4 h-4 mr-2",
								isLoading && "animate-spin",
							)}
						/>
						Refresh
					</Button>
				</div>
			</div>

			{/* Scheduler Health — registry-driven Level A/B board */}
			<CronHealthBoard />

			{/* Section 1: Overview Cards */}
			{summary && Array.isArray(summary) && (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
					{summary.map((job: any) => {
						const run = job.latestRun;
						const status = run?.status ?? "";
						return (
						<Card
							key={job.jobName}
							className={cn(
								"border-2 cursor-pointer hover:shadow-md transition-shadow",
								status === "completed"
									? "border-green-200"
									: status === "partial"
										? "border-yellow-200"
										: status === "failed"
											? "border-red-200"
											: status === "running"
												? "border-blue-200"
												: "border-gray-200",
							)}
							onClick={() => {
								if (run) setDrillDownRun(run);
							}}
						>
							<CardHeader className="pb-2">
								<CardTitle className="flex items-center justify-between text-sm">
									<span className="truncate">{job.jobName}</span>
									{statusIcons[status] || (
										<Clock className="w-4 h-4 text-gray-400" />
									)}
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									<Badge
										className={
											statusColors[status] ||
											"bg-gray-100 text-gray-700"
										}
									>
										{status || "no runs"}
									</Badge>
									{run?.startedAt && (
										<p className="text-xs text-gray-500">
											{formatTimeAgo(run.startedAt)}
										</p>
									)}
									{run?.duration != null && (
										<p className="text-xs text-gray-500">
											Duration: {formatDuration(run.duration)}
										</p>
									)}
									<div className="flex gap-2 text-xs">
										{run?.successCount != null && (
											<span className="text-green-600">
												{run.successCount} ok
											</span>
										)}
										{run?.failedCount != null && run.failedCount > 0 && (
											<button
												type="button"
												className="text-red-600 underline"
												onClick={(e) => {
													e.stopPropagation();
													setDrillDownRun(run);
												}}
											>
												{run.failedCount} failed
											</button>
										)}
									</div>
								</div>
							</CardContent>
						</Card>
						);
					})}
				</div>
			)}

			{/* Section 2: Filters */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Execution Logs</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex gap-4 mb-4">
						<div className="w-64">
							<Label className="text-sm text-gray-600">Job Name</Label>
							<Select
								value={jobNameFilter}
								onValueChange={(v) => {
									setJobNameFilter(v === "all" ? "" : v);
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All jobs" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All jobs</SelectItem>
									<SelectItem value="attendance-finalization-check">Attendance Finalization</SelectItem>
									<SelectItem value="session-cleanup">Session Cleanup</SelectItem>
									<SelectItem value="screenshot-retention-cleanup">Screenshot Retention</SelectItem>
									<SelectItem value="screenshot-alert-check">Screenshot Alert</SelectItem>
									<SelectItem value="ai-classification-batch">AI Classification</SelectItem>
									<SelectItem value="daily-kpi-recalculation">KPI Daily</SelectItem>
									<SelectItem value="monthly-kpi-calculation">KPI Monthly</SelectItem>
									<SelectItem value="daily-kpi-alert-check">KPI Alert</SelectItem>
									<SelectItem value="daily-kpi-ranking-update">KPI Ranking</SelectItem>
									<SelectItem value="screenshot-analysis-scheduler">Screenshot Analysis</SelectItem>
									<SelectItem value="ai-analysis-gap-check">AI Analysis Gap</SelectItem>
									<SelectItem value="report-delivery-check">Report Delivery</SelectItem>
									<SelectItem value="attendance-closer">Attendance Closer</SelectItem>
									<SelectItem value="recurring-bonus-check">Recurring Bonus</SelectItem>
									<SelectItem value="timesheet-materialization-check">Timesheet Materialization</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="w-48">
							<Label className="text-sm text-gray-600">Status</Label>
							<Select
								value={statusFilter}
								onValueChange={(v) => {
									setStatusFilter(v === "all" ? "" : v);
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All statuses" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All statuses</SelectItem>
									<SelectItem value="completed">Completed</SelectItem>
									<SelectItem value="partial">Partial</SelectItem>
									<SelectItem value="failed">Failed</SelectItem>
									<SelectItem value="running">Running</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>

					{/* Log Table */}
					<div className="border rounded-lg overflow-hidden">
						<table className="w-full text-sm">
							<thead className="bg-gray-50 border-b">
								<tr>
									<th className="text-left px-4 py-3 font-medium text-gray-600">
										Job Name
									</th>
									<th className="text-left px-4 py-3 font-medium text-gray-600">
										Status
									</th>
									<th className="text-left px-4 py-3 font-medium text-gray-600">
										Duration
									</th>
									<th className="text-left px-4 py-3 font-medium text-gray-600">
										Orgs
									</th>
									<th className="text-left px-4 py-3 font-medium text-gray-600">
										Started
									</th>
									<th className="text-left px-4 py-3 font-medium text-gray-600">
										Actions
									</th>
								</tr>
							</thead>
							<tbody>
								{logs?.logs?.length > 0 ? (
									logs.logs.map((log: any) => (
										<tr
											key={log._id}
											className="border-b last:border-0 hover:bg-gray-50"
										>
											<td className="px-4 py-3 font-medium">
												{log.jobName}
											</td>
											<td className="px-4 py-3">
												<Badge
													className={
														statusColors[log.status] ||
														"bg-gray-100 text-gray-700"
													}
												>
													{log.status}
												</Badge>
											</td>
											<td className="px-4 py-3 text-gray-600">
												{log.duration != null
													? formatDuration(log.duration)
													: "-"}
											</td>
											<td className="px-4 py-3 text-gray-600">
												{log.totalOrgs != null && (
													<span>
														{log.successCount}/{log.totalOrgs}
													</span>
												)}
											</td>
											<td className="px-4 py-3 text-gray-500">
												{log.startedAt
													? formatTimeAgo(log.startedAt)
													: "-"}
											</td>
											<td className="px-4 py-3">
												<Button
													variant="ghost"
													size="sm"
													onClick={() => setDrillDownRun(log)}
												>
													Details
												</Button>
											</td>
										</tr>
									))
								) : (
									<tr>
										<td
											colSpan={6}
											className="px-4 py-8 text-center text-gray-500"
										>
											{logsLoading
												? "Loading..."
												: "No execution logs found"}
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					{/* Pagination */}
					{logs?.pages != null && (
						<div className="flex items-center justify-between mt-4">
							<p className="text-sm text-gray-500">
								Page {logs.page} of {logs.pages} ({logs.total}{" "}
								total)
							</p>
							<div className="flex gap-2">
								<Button
									variant="outline"
									size="sm"
									disabled={page <= 1}
									onClick={() => setPage((p) => Math.max(1, p - 1))}
								>
									<ChevronLeft className="w-4 h-4" />
								</Button>
								<Button
									variant="outline"
									size="sm"
									disabled={page >= (logs.pages || 1)}
									onClick={() => setPage((p) => p + 1)}
								>
									<ChevronRight className="w-4 h-4" />
								</Button>
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Section 3: Failing Orgs */}
			{failingOrgs && Array.isArray(failingOrgs) && failingOrgs.length > 0 && (
				<Card className="border-2 border-red-200">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-lg text-red-700">
							<AlertTriangle className="w-5 h-5" />
							Failing Organizations
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="border rounded-lg overflow-hidden">
							<table className="w-full text-sm">
								<thead className="bg-red-50 border-b">
									<tr>
										<th className="text-left px-4 py-3 font-medium text-gray-600">
											Organization
										</th>
										<th className="text-left px-4 py-3 font-medium text-gray-600">
											Consecutive Failures
										</th>
										<th className="text-left px-4 py-3 font-medium text-gray-600">
											Last Error
										</th>
										<th className="text-left px-4 py-3 font-medium text-gray-600">
											Last Failure
										</th>
									</tr>
								</thead>
								<tbody>
									{failingOrgs.map((org: any) => (
										<tr
											key={org.organizationId}
											className="border-b last:border-0"
										>
											<td className="px-4 py-3 font-medium">
												{org.orgName || org.organizationId}
											</td>
											<td className="px-4 py-3">
												<Badge className="bg-red-100 text-red-700">
													{org.failures}x
												</Badge>
											</td>
											<td className="px-4 py-3 text-gray-600 max-w-xs truncate">
												{org.lastError || "-"}
											</td>
											<td className="px-4 py-3 text-gray-500">
												{org.lastFailedAt
													? formatTimeAgo(org.lastFailedAt)
													: "-"}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Section 4: Trigger Dialog */}
			<Dialog open={triggerDialogOpen} onOpenChange={setTriggerDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Trigger Job Manually</DialogTitle>
						<DialogDescription>
							Run a job for a specific organization and date.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label>Job Type</Label>
							<Select
								value={triggerType}
								onValueChange={setTriggerType}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="attendance">
										attendance
									</SelectItem>
									<SelectItem value="kpi-daily">kpi-daily</SelectItem>
									<SelectItem value="screenshot-analysis">
										screenshot-analysis
									</SelectItem>
									<SelectItem value="regenerate-reports">
										regenerate-reports
									</SelectItem>
									<SelectItem value="deliver-reports">
										deliver-reports
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label>Organization</Label>
							<Select
								value={triggerOrgId}
								onValueChange={setTriggerOrgId}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select organization" />
								</SelectTrigger>
								<SelectContent>
									{orgList.map((org: any) => (
										<SelectItem
											key={org._id}
											value={org._id}
										>
											{org.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label>Target Date</Label>
							<Input
								type="date"
								value={triggerDate}
								onChange={(e) => setTriggerDate(e.target.value)}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setTriggerDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button
							disabled={
								!triggerOrgId ||
								!triggerDate ||
								triggerMutation.isPending
							}
							onClick={() =>
								triggerMutation.mutate({
									type: triggerType,
									organizationId: triggerOrgId,
									targetDate: triggerDate,
								})
							}
						>
							{triggerMutation.isPending ? "Triggering..." : "Trigger"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Section 5: Backfill Dialog */}
			<Dialog
				open={backfillDialogOpen}
				onOpenChange={setBackfillDialogOpen}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Backfill Job</DialogTitle>
						<DialogDescription>
							Re-run job processing for a date range.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label>Job Type</Label>
							<Select
								value={backfillType}
								onValueChange={setBackfillType}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="attendance">
										Attendance Finalization
									</SelectItem>
									<SelectItem value="kpi-daily">
										KPI Daily Recalculation
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label>Organization</Label>
							<Select
								value={backfillOrgId}
								onValueChange={setBackfillOrgId}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select organization" />
								</SelectTrigger>
								<SelectContent>
									{orgList.map((org: any) => (
										<SelectItem
											key={org._id}
											value={org._id}
										>
											{org.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label>Start Date</Label>
							<Input
								type="date"
								value={backfillStartDate}
								onChange={(e) =>
									setBackfillStartDate(e.target.value)
								}
							/>
						</div>
						<div>
							<Label>End Date</Label>
							<Input
								type="date"
								value={backfillEndDate}
								onChange={(e) =>
									setBackfillEndDate(e.target.value)
								}
							/>
						</div>
						{backfillDays > 0 && (
							<div
								className={cn(
									"p-3 rounded-lg text-sm",
									backfillDays > 30
										? "bg-red-50 text-red-700"
										: "bg-yellow-50 text-yellow-700",
								)}
							>
								<AlertTriangle className="w-4 h-4 inline mr-1" />
								This will process {backfillDays} day
								{backfillDays > 1 ? "s" : ""} of attendance data.
								{backfillDays > 30 &&
									" This is a large range and may take a while."}
							</div>
						)}
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setBackfillDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button
							disabled={
								!backfillOrgId ||
								!backfillStartDate ||
								!backfillEndDate ||
								backfillMutation.isPending
							}
							onClick={() =>
								backfillMutation.mutate({
									type: backfillType,
									organizationId: backfillOrgId,
									startDate: backfillStartDate,
									endDate: backfillEndDate,
								})
							}
						>
							{backfillMutation.isPending
								? "Starting..."
								: "Start Backfill"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Section 6: Cleanup Dialog */}
			<Dialog
				open={cleanupDialogOpen}
				onOpenChange={(open) => {
					setCleanupDialogOpen(open);
					if (!open) {
						setCleanupPreviewCount(null);
						setCleanupConfirmed(false);
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Cleanup Execution Logs</DialogTitle>
						<DialogDescription>
							Delete old execution logs to free up space. The system
							auto-deletes logs older than 30 days, but you can
							manually clean up earlier.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label>Older than</Label>
							<Select
								value={cleanupDays}
								onValueChange={(v) => {
									setCleanupDays(v);
									setCleanupPreviewCount(null);
									setCleanupConfirmed(false);
								}}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="1">1 day</SelectItem>
									<SelectItem value="3">3 days</SelectItem>
									<SelectItem value="7">7 days</SelectItem>
									<SelectItem value="14">14 days</SelectItem>
									<SelectItem value="30">30 days</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label>Job Name (optional)</Label>
							<Select
								value={cleanupJobName || "all"}
								onValueChange={(v) => {
									setCleanupJobName(v === "all" ? "" : v);
									setCleanupPreviewCount(null);
									setCleanupConfirmed(false);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All jobs" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All jobs</SelectItem>
									<SelectItem value="attendance-finalization-check">Attendance Finalization</SelectItem>
									<SelectItem value="session-cleanup">Session Cleanup</SelectItem>
									<SelectItem value="screenshot-retention-cleanup">Screenshot Retention</SelectItem>
									<SelectItem value="screenshot-alert-check">Screenshot Alert</SelectItem>
									<SelectItem value="ai-classification-batch">AI Classification</SelectItem>
									<SelectItem value="daily-kpi-recalculation">KPI Daily</SelectItem>
									<SelectItem value="monthly-kpi-calculation">KPI Monthly</SelectItem>
									<SelectItem value="daily-kpi-alert-check">KPI Alert</SelectItem>
									<SelectItem value="daily-kpi-ranking-update">KPI Ranking</SelectItem>
									<SelectItem value="screenshot-analysis-scheduler">Screenshot Analysis</SelectItem>
									<SelectItem value="ai-analysis-gap-check">AI Analysis Gap</SelectItem>
									<SelectItem value="report-delivery-check">Report Delivery</SelectItem>
									<SelectItem value="attendance-closer">Attendance Closer</SelectItem>
									<SelectItem value="recurring-bonus-check">Recurring Bonus</SelectItem>
									<SelectItem value="timesheet-materialization-check">Timesheet Materialization</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label>Status (optional)</Label>
							<Select
								value={cleanupStatus || "all"}
								onValueChange={(v) => {
									setCleanupStatus(v === "all" ? "" : v);
									setCleanupPreviewCount(null);
									setCleanupConfirmed(false);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All statuses" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All statuses</SelectItem>
									<SelectItem value="completed">Completed</SelectItem>
									<SelectItem value="partial">Partial</SelectItem>
									<SelectItem value="failed">Failed</SelectItem>
									<SelectItem value="running">Running (stuck)</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{cleanupPreviewCount !== null && (
							<div
								className={cn(
									"p-3 rounded-lg text-sm",
									cleanupPreviewCount === 0
										? "bg-gray-50 text-gray-600"
										: "bg-red-50 text-red-700",
								)}
							>
								{cleanupPreviewCount === 0 ? (
									"No logs match the selected criteria."
								) : (
									<>
										<AlertTriangle className="w-4 h-4 inline mr-1" />
										<strong>{cleanupPreviewCount}</strong> log
										{cleanupPreviewCount > 1 ? "s" : ""} will
										be permanently deleted.
									</>
								)}
							</div>
						)}
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setCleanupDialogOpen(false)}
						>
							Cancel
						</Button>
						{!cleanupConfirmed ? (
							<Button
								variant="outline"
								disabled={cleanupPreviewMutation.isPending}
								onClick={() => {
									cleanupPreviewMutation.mutate({
										olderThanDays: Number(cleanupDays),
										jobName: cleanupJobName || undefined,
										status: cleanupStatus || undefined,
									});
								}}
							>
								{cleanupPreviewMutation.isPending
									? "Checking..."
									: "Preview"}
							</Button>
						) : null}
						{cleanupPreviewCount !== null &&
							cleanupPreviewCount > 0 &&
							!cleanupConfirmed && (
								<Button
									variant="destructive"
									onClick={() => setCleanupConfirmed(true)}
								>
									Confirm Delete
								</Button>
							)}
						{cleanupConfirmed && (
							<Button
								variant="destructive"
								disabled={cleanupMutation.isPending}
								onClick={() =>
									cleanupMutation.mutate({
										olderThanDays: Number(cleanupDays),
										jobName: cleanupJobName || undefined,
										status: cleanupStatus || undefined,
									})
								}
							>
								{cleanupMutation.isPending
									? "Deleting..."
									: `Delete ${cleanupPreviewCount} logs`}
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Section 7: Drill-Down Dialog */}
			<Dialog
				open={!!drillDownRun}
				onOpenChange={(open) => {
					if (!open) setDrillDownRun(null);
				}}
			>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							Execution Details{" "}
							{drillDownRun?.jobName && `- ${drillDownRun.jobName}`}
						</DialogTitle>
						<DialogDescription>
							Per-organization execution results for this run.
						</DialogDescription>
					</DialogHeader>
					{drillDownRun && (
						<div className="space-y-4">
							<div className="flex gap-4 text-sm">
								<div>
									<span className="text-gray-500">Status: </span>
									<Badge
										className={
											statusColors[drillDownRun.status] ||
											"bg-gray-100 text-gray-700"
										}
									>
										{drillDownRun.status}
									</Badge>
								</div>
								{drillDownRun.duration != null && (
									<div>
										<span className="text-gray-500">
											Duration:{" "}
										</span>
										<span className="font-medium">
											{formatDuration(drillDownRun.duration)}
										</span>
									</div>
								)}
								{drillDownRun.startedAt && (
									<div>
										<span className="text-gray-500">
											Started:{" "}
										</span>
										<span className="font-medium">
											{new Date(
												drillDownRun.startedAt,
											).toLocaleString()}
										</span>
									</div>
								)}
							</div>

							{drillDownRun.orgExecutions &&
							drillDownRun.orgExecutions.length > 0 ? (
								<div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
									<table className="w-full text-sm">
										<thead className="bg-gray-50 border-b sticky top-0">
											<tr>
												<th className="text-left px-4 py-2 font-medium text-gray-600">
													Organization
												</th>
												<th className="text-left px-4 py-2 font-medium text-gray-600">
													Status
												</th>
												<th className="text-left px-4 py-2 font-medium text-gray-600">
													Duration
												</th>
												<th className="text-left px-4 py-2 font-medium text-gray-600">
													Error
												</th>
											</tr>
										</thead>
										<tbody>
											{drillDownRun.orgExecutions.map(
												(exec: any, idx: number) => (
													<tr
														key={exec.organizationId || idx}
														className="border-b last:border-0"
													>
														<td className="px-4 py-2 font-medium">
															{exec.organizationName || exec.organizationId}
														</td>
														<td className="px-4 py-2">
															<Badge
																className={
																	statusColors[
																		exec.status
																	] ||
																	"bg-gray-100 text-gray-700"
																}
															>
																{exec.status}
															</Badge>
														</td>
														<td className="px-4 py-2 text-gray-600">
															{exec.duration != null
																? formatDuration(
																		exec.duration,
																	)
																: "-"}
														</td>
														<td className="px-4 py-2 text-red-600 max-w-xs truncate text-xs">
															{exec.error || "-"}
														</td>
													</tr>
												),
											)}
										</tbody>
									</table>
								</div>
							) : (
								<p className="text-gray-500 text-center py-4">
									{drillDownRun?.jobName === "session-cleanup"
										? "Job-level tracking only — no per-org breakdown for this job."
										: "No per-org execution data available"}
								</p>
							)}
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
